import { useEffect, useState } from 'react';
import api from '../lib/adminApi';
import config from '../config';
export const endpoint = `${config.apiBaseUrl}/admin-dashboard/operations`;
export const message = (error: any) =>
  error?.response?.data?.error || 'Couldn’t load this information. Try again.';
export type Period = {
  start: string;
  end: string;
  includeTestData: boolean;
};
export const dateKey = (date: Date) => date.toISOString().slice(0, 10);
export function recentPeriod(days: number): Period {
  const end = new Date(),
    start = new Date(end);
  start.setUTCDate(start.getUTCDate() - days + 1);
  return {
    start: dateKey(start),
    end: dateKey(end),
    includeTestData: false,
  };
}
export function useReport<T>(path: string, params: Record<string, unknown>, refresh: number) {
  const [state, setState] = useState<{
    data?: T;
    error?: string;
    loading: boolean;
  }>({
    loading: true,
  });
  const query = JSON.stringify(params);
  useEffect(() => {
    const controller = new AbortController();
    setState({
      loading: true,
    });
    api
      .get(`${endpoint}${path}`, {
        params: {
          ...JSON.parse(query),
          ...(refresh
            ? {
                refresh: true,
              }
            : {}),
        },
        signal: controller.signal,
      })
      .then((response) => {
        if (!controller.signal.aborted)
          setState({
            data: response.data,
            loading: false,
          });
      })
      .catch((error) => {
        if (!controller.signal.aborted)
          setState({
            error: message(error),
            loading: false,
          });
      });
    return () => controller.abort();
  }, [path, query, refresh]);
  return state;
}
export const money = (minor: number, currency: string) => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  });
  return formatter.format(minor / 10 ** (formatter.resolvedOptions().maximumFractionDigits || 0));
};
export const amount = (value: number | null, currency: string) =>
  value === null || !Number.isFinite(value)
    ? 'Not available'
    : new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: /^[A-Z]{3}$/.test(currency) ? currency : 'USD',
      }).format(value);
export const dateTime = (value?: string) =>
  value && Number.isFinite(Date.parse(value))
    ? new Date(value).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZone: 'UTC',
      }) + ' UTC'
    : 'Not recorded';
export const humanize = (value?: string) => (value || 'unknown').replace(/_/g, ' ');
export type BillRow = {
  id: string;
  title: string;
  userId: string;
  creator: string;
  currency: string;
  total: number | null;
  status: string;
  createdAt: string;
  processingStatus: string;
  processingError?: string;
  isTestData: boolean;
  review?: Review;
  auditId?: string;
  role?: string;
};
export type Review = {
  outcome: 'accurate' | 'needs_correction';
  note: string;
  reviewedAt: string;
};
export type Person = {
  id: string;
  name: string;
  username: string | null;
  email: string | null;
  phoneNumber: string | null;
  kind: string;
  status: string;
  createdAt: string;
  admin: boolean;
  canRestore: boolean;
  controlRevision: number;
  receiptBillLimit: number;
  receiptBillsUsed: number;
  receivingProviders: string[];
  suspensionReason: string | null;
  billCount?: number;
  lastBillAt?: string;
};
export type Page<T> = {
  items: T[];
  total: number;
  nextOffset: number | null;
  asOf: string;
  warnings?: string[];
};
